import React from 'react';
import PropTypes from 'prop-types';

import styles from './styles.scss';

/**
 * A progress bar that can be broken up into distinct steps.
 * It can progress up to a step or progress within a step.
 * Currently used on the Order Status page to show order progress.
 */
export default function OrderStatusProgressBar({ steps, progress, currentProgressPercentage }) {
  return (
    <div className={styles['order-status-progress-bar']}>
      <div
        className={styles['current-percentage']}
        style={{ width: `${currentProgressPercentage}%` }}
      />
      {progress.animationType === 'PULSE' && (
        <div
          className={styles.pulse}
          style={{
            left: `${progress.startAtPercentage}%`,
            width: `${progress.finishAtPercentage - progress.startAtPercentage}%`,
            animationDuration: `${progress.duration}ms`,
          }}
        />
      )}
      {progress.animationType === 'PULSE_OVERLAY' && (
        <div>
          <div
            className={styles['pulse-overlay-background']}
            style={{
              left: `${progress.startAtPercentage}%`,
              width: `${progress.finishAtPercentage - progress.startAtPercentage}%`,
            }}
          />
          <div
            className={styles['pulse-overlay']}
            style={{
              left: `${progress.startAtPercentage}%`,
              width: `${progress.finishAtPercentage - progress.startAtPercentage}%`,
              animationDuration: `${progress.duration}ms`,
            }}
          />
        </div>
      )}
      {steps.map((step) => {
        if (step.endsAtProgressPercentage === 100) {
          return null;
        }
        return (
          <div
            key={step.endsAtProgressPercentage}
            className={styles['step-divider']}
            style={{ left: `${step.endsAtProgressPercentage}%` }}
          />
        );
      })}
    </div>
  );
}

OrderStatusProgressBar.propTypes = {
  /**
   * Defines the animation of the progress bar.
   * The `startAtPercentage` and `finishAtPercentage` define the limits
   * of the bar animation (usuall at step points).
   * The `duration` is the speed of the animation.
   * The `animationType` has two styles; the regular `PULSE` which overlays
   * the background of the progress bar to denote which step you're on and
   * the `PULSE_OVERLAY` which overlays a portion of the current progress
   * to denote that the progress is advancing within a step.
   */
  progress: PropTypes.shape({
    startAtPercentage: PropTypes.number,
    animationType: PropTypes.oneOf(['PULSE', 'PULSE_OVERLAY']).isRequired,
    duration: PropTypes.number,
    finishAtPercentage: PropTypes.number,
  }).isRequired,
  /**
   * An array of steps detailing how big each step is.
   */
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      endsAtProgressPercentage: PropTypes.number.isRequired,
    }),
  ).isRequired,
  /**
   * The overall progress across all the steps for the full bar out of 100.
   */
  currentProgressPercentage: PropTypes.number.isRequired,
};
